import { FuelLocation, FuelPlanFuelStop } from "types";

const FUEL_PLAN_SOURCE = "fuel-plan";
const DESIGNATED_ROUTE_SOURCE = "designated-route";
const FUEL_PLAN_OVERVIEW_MODE = "overview";

export type FuelPlanMapMode = "recommendation" | "overview";

export type FuelPlanMapContext = {
  loadNumber: string;
  mode: FuelPlanMapMode;
  sequence: number | null;
  fuelStopIds: string[];
  selectedFuelStopIds: string[];
};

export type FuelPlanReturnSelection = {
  sequence: number;
  fuelStopId: string;
};

type FuelPlanMapUrlOptions = {
  loadNumber: string;
  sequence: number;
  fuelStops: Array<Pick<FuelPlanFuelStop, "id">>;
  selectedFuelStopId?: string;
};

type FuelPlanOverviewMapUrlOptions = {
  loadNumber: string;
  fuelStops: Array<Pick<FuelPlanFuelStop, "id">>;
};

const getUniqueIds = (ids: string[]): string[] =>
  ids.filter((id, index) => id && ids.indexOf(id) === index);

export const buildFuelPlanMapUrl = ({
  loadNumber,
  sequence,
  fuelStops,
  selectedFuelStopId,
}: FuelPlanMapUrlOptions): string | null => {
  const fuelStopIds = getUniqueIds(
    fuelStops.map((fuelStop) => String(fuelStop.id))
  );

  if (!loadNumber || sequence <= 0 || fuelStopIds.length === 0) return null;

  const params = new URLSearchParams();
  params.set("source", FUEL_PLAN_SOURCE);
  params.set("loadNumber", loadNumber);
  params.set("fuelPlanSequence", String(sequence));
  fuelStopIds.forEach((fuelStopId) => params.append("fuelStopId", fuelStopId));

  if (selectedFuelStopId && fuelStopIds.includes(String(selectedFuelStopId))) {
    params.set("selectedFuelStopId", String(selectedFuelStopId));
  }

  return `/preferredRoute?${params.toString()}`;
};

export const buildFuelPlanOverviewMapUrl = ({
  loadNumber,
  fuelStops,
}: FuelPlanOverviewMapUrlOptions): string | null => {
  const fuelStopIds = getUniqueIds(
    fuelStops.map((fuelStop) => String(fuelStop.id))
  );

  if (!loadNumber || fuelStopIds.length === 0) return null;

  const params = new URLSearchParams();
  params.set("source", FUEL_PLAN_SOURCE);
  params.set("fuelPlanMapMode", FUEL_PLAN_OVERVIEW_MODE);
  params.set("loadNumber", loadNumber);
  fuelStopIds.forEach((fuelStopId) => {
    params.append("fuelStopId", fuelStopId);
    params.append("selectedFuelStopId", fuelStopId);
  });

  return `/preferredRoute?${params.toString()}`;
};

export const parseFuelPlanMapContext = (
  search: string
): FuelPlanMapContext | null => {
  const params = new URLSearchParams(search);
  const loadNumber = params.get("loadNumber") ?? "";
  const isOverview = params.get("fuelPlanMapMode") === FUEL_PLAN_OVERVIEW_MODE;
  const sequence = isOverview ? null : Number(params.get("fuelPlanSequence"));
  const fuelStopIds = getUniqueIds(params.getAll("fuelStopId"));
  const selectedFuelStopIds = getUniqueIds(
    params
      .getAll("selectedFuelStopId")
      .filter((fuelStopId) => fuelStopIds.includes(fuelStopId))
  );

  if (
    params.get("source") !== FUEL_PLAN_SOURCE ||
    !loadNumber ||
    (!isOverview &&
      (!Number.isInteger(sequence) || (sequence as number) <= 0)) ||
    fuelStopIds.length === 0
  ) {
    return null;
  }

  return {
    loadNumber,
    mode: isOverview ? "overview" : "recommendation",
    sequence,
    fuelStopIds,
    selectedFuelStopIds,
  };
};

export const buildFuelPlanReturnUrl = (
  loadNumber: string,
  selection?: FuelPlanReturnSelection
): string => {
  const params = new URLSearchParams();
  params.set("loadNumber", loadNumber);

  if (selection) {
    params.set("source", DESIGNATED_ROUTE_SOURCE);
    params.set("fuelPlanSequence", String(selection.sequence));
    params.set("selectedFuelStopId", selection.fuelStopId);
  }

  return `/fuel-plan?${params.toString()}`;
};

export const parseFuelPlanReturnSelection = (
  search: string,
  loadNumber: string
): FuelPlanReturnSelection | null => {
  const params = new URLSearchParams(search);
  const sequence = Number(params.get("fuelPlanSequence"));
  const fuelStopId = params.get("selectedFuelStopId") ?? "";

  if (
    params.get("source") !== DESIGNATED_ROUTE_SOURCE ||
    params.get("loadNumber") !== loadNumber ||
    !Number.isInteger(sequence) ||
    sequence <= 0 ||
    !fuelStopId
  ) {
    return null;
  }

  return { sequence, fuelStopId };
};

export const filterFuelLocationsById = (
  fuelLocations: FuelLocation[],
  fuelStopIds: string[]
): FuelLocation[] => {
  const locationsById = new Map(
    fuelLocations.map((location) => [String(location.id), location])
  );

  return fuelStopIds
    .map((fuelStopId) => locationsById.get(String(fuelStopId)))
    .filter((location): location is FuelLocation => Boolean(location));
};
